import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import BlocksContents from '../Template/BlocksContents';
import BlocksModal from './BlocksSelectionModal';
import Template from '../../../models/Template/Template';
import BlockData from '../../../models/BlockData';
import ModalComponent from '../../../components/ModalComponent';
import { CategoriesContext } from '../../../contexts/CategoriesContext';
import { ImagesContext } from '../../../contexts/ImagesContext';
import { getTemplate } from '../../../services/template';

interface Props {
  open: boolean
  template: Template | null
  handleClose: () => void
  handleSubmit: (template: Template) => void
}

const systemBlockRange: Array<'system-block' | 'my-block'> = ['system-block'];

function Index(props:Props) {
  const {
    open, handleClose, handleSubmit, template,
  } = props;

  // システムブロックモーダルをコントロールするフラグ
  const [systemBlocksModalOpen, setSystemBlocksModalOpen] = useState(false);

  // システムブロックを選んだら、最終確認へ
  const [templateToBeConfirmed, setTemplateToBeConfirmed] = useState<Template | null>(null);

  // 一覧の編集ボランから入ってきた場合は、blockIdがつきます。
  const [blockId, setBlockId] = useState('');
  const [blockTitle, setBlockTitle] = useState('');
  const [blockDesc, setBlockDesc] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [systemBlockName, setSystemBlockName] = useState('');
  const [blockData, setBlockData] = useState<BlockData | null>(null);
  const [imgId, setImgId] = useState(-1);
  const [blockImageUrl, setBlockImageUrl] = useState('');

  const { categories } = useContext(CategoriesContext);

  const { exchangeForUrl } = useContext(ImagesContext);

  // カテゴリータイトル、選んだシステムブロックのカテゴリーにあわせます。
  const categoryTitle = useMemo(() => {
    const category = categories.find((c) => c.id === categoryId);
    if (category == null) {
      return 'マイブロック';
    }
    return category.title;
  }, [categories, categoryId]);

  useEffect(() => {
    if (open) {
      return;
    }
    // モーダルが閉じられた時に
    setTemplateToBeConfirmed(null);

    setBlockId('');
    setBlockTitle('');
    setBlockDesc('');
    setBlockData(null);
    setCategoryId('');
    setSystemBlockName('');
    setImgId(-1);
    setBlockImageUrl('');
  }, [open, template]);

  useEffect(() => {
    if (template?.id == null) {
      return;
    }
    getTemplate(template.id).then((response) => {
      setBlockData(response.data.data.template);
    });
  }, [template]);

  // 必須項目がない場合は、警告表示
  const lackInfoError = useMemo(() => {
    if (blockTitle == null || blockTitle.trim().length === 0) {
      return true;
    }

    if (blockData == null) {
      return true;
    }

    return false;
  }, [blockTitle, blockData]);

  // 編集から入ってきた場合には
  useEffect(() => {
    if (template == null) {
      return;
    }
    setBlockId(template.id);
    setBlockTitle(template.title);
    setBlockDesc(template.description);
    setSystemBlockName(template.source_template_name);
    // TODO: set category name
    // deepcopyして、上層のtemplateとunbindします。
    setCategoryId(template.categoryId);
    setImgId(template.img_id);

    // for security concern, exchange for a safe url
    exchangeForUrl(template.img_url).then((url) => {
      setBlockImageUrl(url);
    });
  }, [template, exchangeForUrl]);

  // システムブロックモーダルから戻ってきた場合
  const handleTemplateSelectedFromSystemBlockModal = (template: Template) => {
    // deepcopyして、上層のtemplateとunbindします。

    getTemplate(template.id).then((response) => {
      setBlockData(response.data.data.template);
    });
    setCategoryId(template.categoryId);
    setSystemBlockName(template.title);

    exchangeForUrl(template.img_url).then((url) => {
      setBlockImageUrl(url);
    }).catch(() => {
      setBlockImageUrl('');
    });

    // システムのimg_idを使います。
    setImgId(template.img_id);
  };

  // 修正また新規作成のリクエストをなげます
  const handleSave = () => {
    // タイトル入力が必要です。
    if (blockTitle.trim().length === 0) {
      return;
    }

    // ブロックデーターが必要です。
    if (blockData == null) {
      return;
    }

    const data: Template = {
      id: blockId,
      categoryId,
      title: blockTitle,
      description: blockDesc,
      template: blockData,
      type: 'my-block',
      img_id: imgId,
      source_template_name: systemBlockName,
    };
    handleSubmit(data);
  };

  const title = useMemo(() => {
    if (template == null) {
      return 'ブロック作成';
    }

    return 'ブロック編集';
  }, [template]);

  return (
    <>
      <ModalComponent
        size="xl"
        title={title}
        confirmButtonText="保存する"
        cancelButtonText="閉じる"
        onConfirm={handleSave}
        show={open}
        closeModal={handleClose}
        centered
        animation={false}
        isLackOfInfo={lackInfoError}
        scrollable
      >
        <form className="d-flex flex-column h-100">
          <div className="row">
            <div className="col-sm-8">
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">
                  ブロック名
                  <span>*</span>
                </label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione1">
                    <input type="text" className="form-control" placeholder="ブロック名を入力" value={blockTitle} onChange={(e) => setBlockTitle(e.target.value)} />
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback1">ブロック名を入力してください。</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">
                  ブロック説明
                </label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione2">
                    <input type="text" className="form-control" placeholder="ブロック説明を入力" value={blockDesc} onChange={(e) => setBlockDesc(e.target.value)} />
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback2">ブロック説明を入力してください。</div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">カテゴリ</label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione2">
                    <input type="text" className="form-control" value={categoryTitle} readOnly />
                  </div>
                </div>
              </div>
              <div className="row g-3 mb-3">
                <label className="col-form-label col-sm-3 text-sm-start">
                  システムブロック
                  <span>*</span>
                </label>
                <div className="col-sm-9">
                  <div className="is-invalid" id="validatione4">
                    <div className="input-group">
                      <input type="text" className="form-control" value={systemBlockName} placeholder="システムブロック名" aria-describedby="button-baseblock" readOnly />
                      <button className="btn btn-primary" type="button" id="button-baseblock" onClick={() => setSystemBlocksModalOpen(true)}>システムブロック選択 ...</button>
                    </div>
                  </div>
                  <div className="invalid-feedback d-none" id="validationeFeedback4">システムブロックを選択してください。</div>
                </div>
              </div>
            </div>
            <div className="col-sm-4">
              <div className="row g-3 mb-3 h-100">
                <div className="col-12  d-flex flex-column">
                  <div className="block-image-view mt-2 flex-fill">
                    <div className="block-image">
                      <img src={blockImageUrl} alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row g-3 pb-3 flex-fill">
            <div className="col-12">
              <div className="h-100">
                {/* previewの部分 */}
                <BlocksContents
                  blockData={blockData}
                  handleDataChange={(data) => {
                    setBlockData(data);
                  }}
                />
              </div>
            </div>
          </div>
        </form>
      </ModalComponent>
      {systemBlocksModalOpen && (
        <BlocksModal
          title="システムブロックの選択"
          open={systemBlocksModalOpen}
          range={systemBlockRange}
          handleTemplateSelected={(template) => {
            if (blockData == null) {
              handleTemplateSelectedFromSystemBlockModal(template);
              setTemplateToBeConfirmed(null);
              setSystemBlocksModalOpen(false);
              return;
            }
            setTemplateToBeConfirmed(template);
            setSystemBlocksModalOpen(false);
          }}
          handleClose={() => setSystemBlocksModalOpen(false)}
        />
      )}

      <ModalComponent
        size="sm"
        backdrop="static"
        backdropClassName="z-index-2000"
        style={{ zIndex: 2001 }}
        keyboard={false}
        noheader
        centered
        animation={false}
        title=""
        body={(
            <div>
              <div>編集内容が破棄されます。</div>
              <div>よろしいですか？</div>
            </div>
          )}
        confirmButtonText="はい"
        confirmButtonColor="success"
        onConfirm={() => {
          handleTemplateSelectedFromSystemBlockModal(templateToBeConfirmed);
          setTemplateToBeConfirmed(null);
        }}
        show={templateToBeConfirmed != null}
        closeModal={() => {
          setTemplateToBeConfirmed(null);
        }}
      />
    </>
  );
}

export default Index;
